import React, { memo, useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import { getBannerList } from "../../store/slices/user";
import { LUX_PROMOS } from "../../data/luxCatalog";
import styles from "./Slider.module.css";

const AUTOPLAY_MS = 5000;

// mockup banners, used until the admin uploads real ones
const MOCKUP_SLIDES = LUX_PROMOS.map((p) => ({ id: p.id, src: p.img, alt: p.title }));

/**
 * Banner carousel (data from /setting/banner)
 * - swipe on touch, pause on hover / hidden tab
 * - `fallback` = array of {id, src, alt} used when backend has no banners
 */
const Slider = memo(({ className = "", fallback = MOCKUP_SLIDES }) => {
  const dispatch = useDispatch();
  const { bannerList } = useSelector((state) => state.user);
  const [current, setCurrent] = useState(0);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false,
  );
  const [paused, setPaused] = useState(false);
  const [broken, setBroken] = useState(() => new Set());
  const touchX = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    dispatch(getBannerList());
  }, [dispatch]);

  const apiSlides = (Array.isArray(bannerList) ? bannerList : [])
    .filter((b) => b.status === "NORMAL")
    .map((b, i) => {
      const path = isMobile
        ? b.imageUrlMobile || b.imageUrl || b.imageUrlPc
        : b.imageUrlPc || b.imageUrl || b.imageUrlMobile;
      if (!path) return null;
      return {
        id: b.id || i + 1,
        src: /^https?:/i.test(path) ? path : `${process.env.REACT_APP_API_BASE_URL}${path}`,
        alt: b.title || b.bannerName || `Banner ${i + 1}`,
      };
    })
    .filter(Boolean)
    .filter((s) => !broken.has(s.src));

  const slides = apiSlides.length ? apiSlides : fallback;
  const count = slides.length;

  const next = useCallback(() => setCurrent((p) => (p + 1) % Math.max(count, 1)), [count]);
  const prev = useCallback(
    () => setCurrent((p) => (p - 1 + Math.max(count, 1)) % Math.max(count, 1)),
    [count],
  );

  useEffect(() => {
    if (count < 2 || paused) return undefined;
    const t = setInterval(() => {
      if (!document.hidden) next();
    }, AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [count, paused, next, current]);

  useEffect(() => {
    if (current >= count && count > 0) setCurrent(0);
  }, [count, current]);

  if (!count) {
    return <div className={`${styles.slider} ${styles.skeleton} ${className}`} aria-hidden="true" />;
  }

  return (
    <div
      className={`${styles.slider} ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => {
        touchX.current = e.touches[0].clientX;
        setPaused(true);
      }}
      onTouchEnd={(e) => {
        const start = touchX.current;
        touchX.current = null;
        setPaused(false);
        if (start == null) return;
        const dx = e.changedTouches[0].clientX - start;
        if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
      }}
      role="region"
      aria-roledescription="carousel"
      aria-label="แบนเนอร์โปรโมชั่น"
    >
      <div className={styles.track} style={{ transform: `translateX(-${current * 100}%)` }}>
        {slides.map((s, i) => (
          <div
            key={s.id}
            className={styles.slide}
            aria-hidden={i !== current}
            aria-roledescription="slide"
          >
            <img
              src={s.src}
              alt={s.alt}
              className={styles.image}
              loading={i === 0 ? "eager" : "lazy"}
              draggable="false"
              onError={() => setBroken((prev) => new Set(prev).add(s.src))}
            />
          </div>
        ))}
      </div>

      {count > 1 && (
        <>
          <button type="button" className={`${styles.nav} ${styles.prev}`} onClick={prev} aria-label="ก่อนหน้า">
            <Icon icon="solar:alt-arrow-left-linear" width={22} />
          </button>
          <button type="button" className={`${styles.nav} ${styles.next}`} onClick={next} aria-label="ถัดไป">
            <Icon icon="solar:alt-arrow-right-linear" width={22} />
          </button>
          <div className={styles.dots}>
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                className={`${styles.dot} ${i === current ? styles.dotActive : ""}`}
                onClick={() => setCurrent(i)}
                aria-label={`ไปสไลด์ที่ ${i + 1}`}
                aria-current={i === current}
              >
                {i === current && (
                  <span
                    key={`${current}-${paused}`}
                    className={styles.dotFill}
                    style={{
                      animationDuration: `${AUTOPLAY_MS}ms`,
                      animationPlayState: paused ? "paused" : "running",
                    }}
                  />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
});

Slider.displayName = "Slider";

export default Slider;
